import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUserControlsContext } from '../../contexts/UserControlsContext';
import { hasRequiredRole } from '../../lib/roles';

const AdminBlueprints = React.lazy(() => import('../../AdminBlueprints'));
const UnifiedActionsPage = React.lazy(() => import('./actions/UnifiedActionsPage'));
const PlaysPage = React.lazy(() => import('./plays/PlaysPage'));
const RankerPage = React.lazy(() => import('./ranker/RankerPage'));
const AdminRegistries = React.lazy(() => import('../../AdminRegistries'));
const DashboardSettingsTab = React.lazy(() => import('./settings/DashboardSettingsTab'));
const GongReconcileSettingsTab = React.lazy(() => import('./settings/GongReconcileSettingsTab'));

type Tab = 'blueprints' | 'actions' | 'plays' | 'ranker' | 'registries' | 'settings' | 'gong-reconcile';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'blueprints', label: 'Blueprints' },
  { key: 'actions', label: 'Actions' },
  { key: 'plays', label: 'Plays' },
  { key: 'ranker', label: 'Ranker' },
  { key: 'registries', label: 'Registries' },
  { key: 'settings', label: 'Settings' },
  { key: 'gong-reconcile', label: 'Gong reconcile' },
];

/**
 * The route admits `config_editor` OR `flow_admin` (the Ranker's saves need
 * `flow_admin`). Every other tab calls config_editor-gated endpoints, so a
 * Flow admin without config_editor gets the Ranker tab only. Roles empty or
 * not yet loaded keep the full set: the route guard already admitted the
 * caller, and this must never hide tabs on a transient unknown.
 */
export function getAccessibleTabs(roles: string[] | undefined): Tab[] {
  const flowAdminOnly = !hasRequiredRole(roles, ['config_editor']) && hasRequiredRole(roles, ['flow_admin']);
  return flowAdminOnly ? ['ranker'] : TABS.map((t) => t.key);
}

/** Honors a valid, accessible URL tab; else the first accessible one ('blueprints' for full access). */
export function pickActiveTab(requested: string | undefined, accessible: Tab[]): Tab {
  return accessible.find((t) => t === requested) ?? accessible[0] ?? 'blueprints';
}

export default function ConfigurationPage() {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { roles } = useUserControlsContext();
  const accessibleTabs = getAccessibleTabs(roles);
  const activeTab = pickActiveTab(tab, accessibleTabs);
  const setActiveTab = (t: Tab) => navigate(`/admin/configuration/${t}`, { replace: true });

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-1 border-b border-border-secondary flex-shrink-0">
        {TABS.filter((t) => accessibleTabs.includes(t.key)).map((tabEntry) => (
          <button
            key={tabEntry.key}
            onClick={() => setActiveTab(tabEntry.key)}
            className={`px-3 py-3 text-sm border-b-2 ${
              activeTab === tabEntry.key
                ? 'border-blueberry-500 text-content-primary font-medium'
                : 'border-transparent text-icon-subtle hover:text-content-tertiary'
            }`}
          >
            {tabEntry.label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0">
        <React.Suspense fallback={<div className="flex items-center justify-center h-full text-icon-subtle">Loading...</div>}>
          {activeTab === 'blueprints' && <AdminBlueprints />}
          {activeTab === 'actions' && <UnifiedActionsPage />}
          {activeTab === 'plays' && <PlaysPage />}
          {activeTab === 'ranker' && <RankerPage />}
          {activeTab === 'registries' && <AdminRegistries />}
          {activeTab === 'settings' && <DashboardSettingsTab />}
          {activeTab === 'gong-reconcile' && <GongReconcileSettingsTab />}
        </React.Suspense>
      </div>
    </div>
  );
}
